<template>
   <Form ref="formRef" v-model="formData" @submit="handleSubmit" @dirty="(e) => (dirty = e)">
      <Switch grow v-bind="formData.can_split_season" v-model="formData.can_split_season.value" />
      <Switch grow v-bind="formData.can_issue_permits" v-model="formData.can_issue_permits.value" />

      <div class="__form-row">
         <TextInput
            :grow="$isBreakpoint('md', 'smaller')"
            v-bind="formData.total_assigned"
            v-model="formData.total_assigned.value"
         />
         <TextInput
            :grow="$isBreakpoint('md', 'smaller')"
            v-bind="formData.total_physical"
            v-model="formData.total_physical.value"
         />
         <Select
            grow
            v-bind="formData.calculation_mode"
            v-model="formData.calculation_mode.value"
         />
      </div>

      <TextInput
         v-if="type === 'office'"
         grow
         v-bind="formData.max_physical_daily_permits"
         v-model="formData.max_physical_daily_permits.value"
      />

      <div class="__form-row" v-if="formData.calculation_mode.value === 'PCM_MANUAL'">
         <TextInput grow v-bind="formData.split_daily" v-model="formData.split_daily.value" />
         <TextInput grow v-bind="formData.split_season" v-model="formData.split_season.value" />
      </div>

      <Separator />

      <div class="__form-row">
         <TextInput
            grow
            v-bind="formData.remaining_daily"
            v-model="formData.remaining_daily.value"
         />
         <TextInput
            grow
            v-bind="formData.remaining_season"
            v-model="formData.remaining_season.value"
         />
         <TextInput
            grow
            v-bind="formData.remaining_physical"
            v-model="formData.remaining_physical.value"
         />
      </div>

      <div class="__form-row">
         <TextInput
            grow
            v-bind="formData.issued_daily_counter"
            v-model="formData.issued_daily_counter.value"
         />
         <TextInput
            grow
            v-bind="formData.issued_season_counter"
            v-model="formData.issued_season_counter.value"
         />
      </div>

      <div class="__form-row">
         <TextInput
            grow
            v-bind="formData.issued_physical_daily_counter"
            v-model="formData.issued_physical_daily_counter.value"
         />
         <TextInput
            grow
            v-bind="formData.issued_physical_season_counter"
            v-model="formData.issued_physical_season_counter.value"
         />
      </div>
   </Form>
</template>

<script lang="ts" setup>
import type Form from '~/components/core/Form.vue'
import { useUpdateOrganizationPoints } from '~/composables/api/mutations/update-organization-points'
import { useOrganization } from '~/composables/api/queries/organization'

type Props = {
   organizationId?: string
   type: 'organization' | 'office'
}

const props = withDefaults(defineProps<Props>(), {})

const { t } = useI18n()
const rbac = useRbac()

const ORGANIZATION_POINTS_CALCULATION_MODE_OPTIONS = [
   { id: 'PCM_AUTOMATIC', value: t('enum.PCM_AUTOMATIC') },
   { id: 'PCM_MANUAL', value: t('enum.PCM_MANUAL') },
]

const formRef = ref<InstanceType<typeof Form>>()
const dirty = ref<boolean>(false)
const actionDisabled = computed(() => !dirty.value || formDisabled.value)
const formDisabled = computed(() => {
   return (
      (props.type === 'office'
         ? !rbac(['organization:points:create'])
         : !rbac(['organization:points:update'])) ||
      organization.data.value?.[0].organizationpoints?.[0].is_locked
   )
})

const updateOrganizationPoints = useUpdateOrganizationPoints({
   confirmation: {
      type: 'save',
   },
})

const organization = useOrganization(
   computed(() => ({
      id: props.organizationId,
      enabled: !!props.organizationId,
   }))
)

const formData = useFormData(() => ({
   total_assigned: {
      value: organization.data.value?.[0].organizationpoints?.[0].total_assigned,
      label: t('form.organizationpoints-form.total-assigned'),
      type: 'number',
      disabled: formDisabled.value || props.type !== 'office',
   },
   total_physical: {
      value: organization.data.value?.[0].organizationpoints?.[0].total_physical,
      label: t('form.organizationpoints-form.total-physical'),
      type: 'number',
      disabled: formDisabled.value || props.type !== 'office',
   },
   max_physical_daily_permits: {
      value: organization.data.value?.[0].organizationpoints?.[0].max_physical_daily_permits,
      label: t('form.organizationpoints-form.max-physical-daily-permits'),
      type: 'number',
      disabled: formDisabled.value || props.type !== 'office',
   },
   split_daily: {
      value: organization.data.value?.[0].organizationpoints?.[0].split_daily,
      label: t('form.organizationpoints-form.split-daily'),
      type: 'number',
      disabled: formDisabled.value || props.type !== 'organization',
   },
   split_season: {
      value: organization.data.value?.[0].organizationpoints?.[0].split_season,
      label: t('form.organizationpoints-form.split-season'),
      type: 'number',
      disabled: formDisabled.value || props.type !== 'organization',
   },
   calculation_mode: {
      value: organization.data.value?.[0].organizationpoints?.[0].calculation_mode,
      label: t('form.organizationpoints-form.calculation-mode'),
      type: 'select',
      options: ORGANIZATION_POINTS_CALCULATION_MODE_OPTIONS,
      disabled: formDisabled.value || props.type !== 'organization',
   },
   can_split_season: {
      value: organization.data.value?.[0].organizationpoints?.[0].can_split_season,
      label: t('form.organizationpoints-form.can-split-season'),
      type: 'checkbox',
      disabled: formDisabled.value || props.type !== 'office',
   },
   can_issue_permits: {
      value: organization.data.value?.[0].organizationpoints?.[0].can_issue_permits,
      label: t('form.organizationpoints-form.can-issue-permits'),
      type: 'checkbox',
      disabled: formDisabled.value || props.type !== 'office',
   },
   remaining_daily: {
      value: organization.data.value?.[0].organizationpoints?.[0].remaining_daily,
      label: t('form.organizationpoints-form.remaining-daily'),
      type: 'number',
      disabled: true,
   },
   remaining_season: {
      value: organization.data.value?.[0].organizationpoints?.[0].remaining_season,
      label: t('form.organizationpoints-form.remaining-season'),
      type: 'number',
      disabled: true,
   },
   remaining_physical: {
      value: organization.data.value?.[0].organizationpoints?.[0].remaining_physical,
      label: t('form.organizationpoints-form.remaining-physical'),
      type: 'number',
      disabled: true,
   },
   issued_daily_counter: {
      value: organization.data.value?.[0].organizationpoints?.[0].issued_daily_counter,
      label: t('form.organizationpoints-form.issued-daily-counter'),
      type: 'number',
      disabled: true,
   },
   issued_season_counter: {
      value: organization.data.value?.[0].organizationpoints?.[0].issued_season_counter,
      label: t('form.organizationpoints-form.issued-season-counter'),
      type: 'number',
      disabled: true,
   },
   issued_physical_daily_counter: {
      value: organization.data.value?.[0].organizationpoints?.[0].issued_physical_daily_counter,
      label: t('form.organizationpoints-form.issued-physical-daily-counter'),
      type: 'number',
      disabled: true,
   },
   issued_physical_season_counter: {
      value: organization.data.value?.[0].organizationpoints?.[0].issued_physical_season_counter,
      label: t('form.organizationpoints-form.issued-physical-season-counter'),
      type: 'number',
      disabled: true,
   },
}))

const handleSubmit = async (data: typeof formData) => {
   await tryCatch(async () => {
      await updateOrganizationPoints.mutateAsync({
         p_organization_id: organization.data.value?.[0].organizationpoints?.[0].organization_id,
         p_total_assigned: data.total_assigned.value,
         p_total_physical: data.total_physical.value,
         p_max_physical_daily_permits: data.max_physical_daily_permits.value,
         p_split_daily: data.split_daily.value,
         p_split_season: data.split_season.value,
         p_calculation_mode: data.calculation_mode.value,
         p_can_split_season: data.can_split_season.value,
         p_can_issue_permits: data.can_issue_permits.value,
         p_is_organization_mode: props.type === 'organization',
      })
   })
}

defineExpose({
   action: () => formRef.value?.submit(),
   loading: updateOrganizationPoints.isPending,
   disabled: actionDisabled,
})
</script>
